import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { dendrosApi } from "@/lib/dendrosDemo";
import { DendrosSidebar } from "@/components/dendros-pulse/DendrosSidebar";
import {
    Search,
    Bell,
    HelpCircle,
    Settings,
    Phone,
    Mail,
    MapPin,
    ChevronDown,
    GitBranch,
    Building2,
    Star,
} from "lucide-react";

export default function SalesforceMock() {
    const { accountId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(searchParams.get("sidebar") === "1");

    useEffect(() => {
        dendrosApi.listAccounts().then(setAccounts);
    }, []);

    useEffect(() => {
        if (!accountId && accounts.length > 0) {
            navigate(`/salesforce/${accounts[0].id}`, { replace: true });
        }
    }, [accountId, accounts, navigate]);

    useEffect(() => {
        if (accountId) {
            dendrosApi.getAccount(accountId).then(setData).catch(() => setData(null));
        }
    }, [accountId]);

    if (!data) {
        return (
            <div className="sf-surface min-h-screen flex items-center justify-center text-slate-500 font-sf">
                Loading Salesforce…
            </div>
        );
    }

    const acc = data.account;
    const contacts = data.contacts;

    return (
        <div className="sf-surface min-h-screen relative">
            {/* SF Top Nav */}
            <header className="bg-[#16325c] text-white">
                <div className="flex items-center px-4 h-12">
                    <div className="flex items-center gap-2 mr-6">
                        <div className="w-7 h-7 bg-white text-[#16325c] flex items-center justify-center font-bold text-sm rounded">⚡</div>
                        <div className="font-sf font-semibold text-sm">Sales</div>
                        <ChevronDown size={14} />
                    </div>
                    <nav className="flex items-center gap-5 font-sf text-sm">
                        <span className="hover:underline cursor-pointer">Home</span>
                        <span className="hover:underline cursor-pointer">Opportunities</span>
                        <span className="hover:underline cursor-pointer flex items-center gap-1 border-b-2 border-white pb-3 -mb-3">
                            Accounts <ChevronDown size={12} />
                        </span>
                        <span className="hover:underline cursor-pointer">Leads</span>
                        <span className="hover:underline cursor-pointer">Tasks</span>
                        <span className="hover:underline cursor-pointer">Reports</span>
                    </nav>
                    <div className="ml-auto flex items-center gap-4">
                        <div className="relative">
                            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                className="bg-white/10 placeholder-slate-300 text-white text-sm pl-7 pr-3 py-1 rounded w-72 outline-none focus:bg-white/20"
                                placeholder="Search Salesforce"
                            />
                        </div>
                        <Bell size={16} />
                        <HelpCircle size={16} />
                        <Settings size={16} />
                    </div>
                </div>
            </header>

            {/* Account header */}
            <div className="bg-white border-b border-slate-200">
                <div className="px-6 pt-3 font-sf text-xs text-slate-500 flex items-center gap-1">
                    <Building2 size={12} /> Accounts <span className="mx-1">›</span> {acc.name}
                </div>
                <div className="px-6 py-4 flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0070d2] text-white flex items-center justify-center rounded-md font-sf font-semibold text-xl">
                        {acc.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1">
                        <div className="font-sf text-xs text-slate-500 uppercase tracking-wide">Account</div>
                        <h1 className="font-sf text-2xl font-semibold text-slate-800 mt-0.5">{acc.name}</h1>
                        <div className="flex items-center gap-4 mt-2 font-sf text-xs text-slate-500">
                            <span><strong className="text-slate-700">Type</strong> · Customer</span>
                            <span><strong className="text-slate-700">Industry</strong> · {acc.industry}</span>
                            <span><strong className="text-slate-700">Owner</strong> · {acc.owner}</span>
                            <span><strong className="text-slate-700">ARR</strong> · {acc.arr}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="btn-sf-outline">Edit</button>
                        <button className="btn-sf-outline">New Contact</button>
                        <button className="btn-sf-outline">+ Follow</button>
                        <button className="btn-sf"><Star size={12} className="inline mr-1" />Logged Call</button>
                    </div>
                </div>
                <div className="px-6 flex items-center gap-6 border-t border-slate-100 font-sf text-sm">
                    {["Related", "Details", "News", "Activity", "Chatter"].map((t, i) => (
                        <button
                            key={t}
                            className={`py-2.5 ${i === 0 ? "border-b-2 border-[#0070d2] text-[#0070d2] font-medium" : "text-slate-600 hover:text-slate-900"}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            {/* Account selector strip */}
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-2 flex items-center gap-3 overflow-x-auto">
                <span className="font-sf text-xs text-slate-500 uppercase tracking-wide">Demo Accounts:</span>
                {accounts.map((a) => (
                    <button
                        key={a.id}
                        onClick={() => navigate(`/salesforce/${a.id}`)}
                        className={`font-sf text-xs px-3 py-1 rounded ${
                            a.id === accountId ? "bg-[#0070d2] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#0070d2]"
                        }`}
                        data-testid={`select-account-${a.id}`}
                    >
                        {a.name}
                    </button>
                ))}
            </div>

            {/* Body */}
            <div className="p-6 grid grid-cols-3 gap-6">
                <div className="col-span-2 space-y-4">
                    <div className="sf-card">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                            <div className="font-sf font-semibold text-slate-800">Contacts ({contacts.length})</div>
                            <button className="font-sf text-xs text-[#0070d2] hover:underline">View All</button>
                        </div>
                        <table className="w-full font-sf text-sm">
                            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                                <tr>
                                    <th className="text-left px-4 py-2">Name</th>
                                    <th className="text-left px-4 py-2">Title</th>
                                    <th className="text-left px-4 py-2">Email</th>
                                    <th className="text-left px-4 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map((c) => (
                                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="px-4 py-2.5 text-[#0070d2] font-medium">{c.name}</td>
                                        <td className="px-4 py-2.5 text-slate-700">{c.title}</td>
                                        <td className="px-4 py-2.5 text-slate-600">{c.email}</td>
                                        <td className="px-4 py-2.5">
                                            <span
                                                className={`inline-block px-2 py-0.5 rounded text-xs ${
                                                    c.status === "Inactive"
                                                        ? "bg-slate-200 text-slate-600"
                                                        : "bg-emerald-50 text-emerald-700"
                                                }`}
                                            >
                                                {c.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="sf-card p-4">
                        <div className="font-sf font-semibold text-slate-800 mb-2">Opportunities (3)</div>
                        <div className="font-sf text-sm text-slate-500">Renewal Q4 · Expansion · Cross-sell Module</div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="sf-card p-4 space-y-2 font-sf text-sm">
                        <div className="font-semibold text-slate-800 mb-2">Account Details</div>
                        <div className="flex gap-2 text-slate-600">
                            <Phone size={14} className="text-slate-400 mt-0.5" /> +1 (555) 240-9912
                        </div>
                        <div className="flex gap-2 text-slate-600">
                            <Mail size={14} className="text-slate-400 mt-0.5" /> hello@{acc.name.toLowerCase().replaceAll(" ", "")}.com
                        </div>
                        <div className="flex gap-2 text-slate-600">
                            <MapPin size={14} className="text-slate-400 mt-0.5" /> 1100 Market Street, San Francisco
                        </div>
                    </div>
                    <div className="sf-card p-4">
                        <div className="font-sf font-semibold text-slate-800 mb-2">Recent Activity</div>
                        <div className="font-sf text-sm text-slate-500">Synced from Salesforce 2h ago</div>
                    </div>
                </div>
            </div>

            {/* Dendros FAB */}
            {!sidebarOpen && (
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="fixed bottom-8 right-8 z-40 w-16 h-16 rounded-full bg-[#050505] border-2 border-[#10b981] text-[#10b981] flex items-center justify-center dendros-pulse hover:scale-105 transition-transform"
                    aria-label="Open Dendros"
                    data-testid="dendros-fab"
                >
                    <GitBranch size={26} strokeWidth={2.2} />
                </button>
            )}

            {/* Dendros Sidebar */}
            {sidebarOpen && (
                <div className="fixed top-0 right-0 h-full z-50">
                    <DendrosSidebar
                        accountId={accountId}
                        onClose={() => setSidebarOpen(false)}
                        embedded
                    />
                </div>
            )}
        </div>
    );
}
